/**
 * 手動スキャンの依頼(`POST /api/dlp/scans`)。端末を選び、ポリシー(既定が選ばれている)と備考を付ける。
 * 同じ端末に進行中のスキャンがあればサーバーが 409 を返すので、その文言をそのまま出す。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Form, Input, Modal, Select, message } from "antd";
import { useEffect } from "react";

import type { DlpScan } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useDlpPolicies, useInvalidateDlp } from "./hooks";
import { DeviceSelect } from "./selects";

type Values = { device_id: number | null; policy_id: number | null; note?: string };

export function ScanRequestModal({
  open,
  deviceId,
  onClose,
  onCreated,
}: {
  open: boolean;
  /** 端末パネルから開いたときの初期値 */
  deviceId?: number | null;
  onClose: () => void;
  onCreated: (scan: DlpScan) => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateDlp();
  const [form] = Form.useForm<Values>();
  const policies = useDlpPolicies(open);
  const defaultPolicy = policies.data?.items.find((p) => p.is_default);

  // 開くたびに初期値へ戻す(前回の端末が残ると別の端末に依頼してしまう)
  useEffect(() => {
    if (open) form.setFieldsValue({ device_id: deviceId ?? null, policy_id: defaultPolicy?.id ?? null, note: "" });
  }, [open, deviceId, defaultPolicy?.id, form]);

  const create = useMutation({
    mutationFn: (v: Values) =>
      api.dlpRequestScan({ device_id: v.device_id!, policy_id: v.policy_id ?? undefined, note: v.note?.trim() || undefined }),
    onSuccess: (scan) => {
      void message.success("スキャンを依頼した(次の poll でエージェントに渡る)");
      invalidate();
      onCreated(scan);
    },
  });

  return (
    <Modal
      open={open}
      title="スキャンを依頼"
      okText="依頼する"
      cancelText="やめる"
      onCancel={onClose}
      onOk={() => form.submit()}
      okButtonProps={{ loading: create.isPending }}
      destroyOnClose
    >
      {create.isError && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="依頼できなかった" description={errorText(create.error)} />
      )}
      <Form<Values> form={form} layout="vertical" onFinish={(v) => create.mutate(v)} disabled={create.isPending}>
        <Form.Item
          name="device_id"
          label="端末"
          rules={[{ required: true, message: "端末を選ぶ" }]}
          extra="poll してきた端末だけ選べる。進行中のスキャンがある端末には依頼できない(409)"
        >
          <DeviceSelect autoFocus />
        </Form.Item>
        <Form.Item name="policy_id" label="検査ポリシー" extra="渡した内容はスキャンに写す(後からポリシーを変えても結果の意味は変わらない)">
          <Select
            allowClear
            loading={policies.isLoading}
            placeholder={defaultPolicy ? `既定: ${defaultPolicy.name}` : "既定のポリシー"}
            options={(policies.data?.items ?? []).map((p) => ({ value: p.id, label: p.is_default ? `${p.name}(既定)` : p.name }))}
          />
        </Form.Item>
        <Form.Item name="note" label="備考">
          <Input.TextArea rows={2} maxLength={500} placeholder="依頼の理由など(任意)" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
